import { Hive, hiveSchema, Load } from "@fatbees/schemas";
import { toast } from "@fatbees/ui-components";
import { queryCollectionOptions } from "@tanstack/query-db-collection";
import { createCollection } from "@tanstack/react-db";
import { QueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { deleteHive, putHive } from "../api";

const REFETCH_INTERVAL = 1000 * 60 * 15; // 15 minutes
const QUERY_KEY = ["hives"];
const LOADS_QUERY_KEY = ["loads"];

function hiveEntityName(hive: Hive, queryClient: QueryClient): string {
  const loads = queryClient.getQueryData<Load[]>(LOADS_QUERY_KEY);
  const load = loads?.find((l) => l.id === hive.loadId);
  return load?.isScout ? "scout" : "hive";
}

export function useHivesCollectionInstance(queryClient: QueryClient) {
  useEffect(() => {
    // As of TanStack DB v0.5 refetchInterval is unreliable, even with
    // refetchIntervalInBackground set to true.
    const interval = setInterval(() => {
      queryClient.refetchQueries({ queryKey: QUERY_KEY });
    }, REFETCH_INTERVAL);

    return () => clearInterval(interval);
  }, [queryClient]);

  return useMemo(() => {
    return createCollection(
      queryCollectionOptions({
        id: "hives",
        queryKey: QUERY_KEY,
        queryFn: async () => {
          const response = await fetch("/api/hives");
          const body = await response.json();

          return body.map((hive: unknown) => hiveSchema.parse(hive));
        },
        queryClient,
        getKey: (item) => item.id,
        schema: hiveSchema,
        onInsert: async ({ transaction, collection }) => {
          try {
            await Promise.all(
              transaction.mutations.map(async ({ modified }) => {
                await putHive(modified);

                collection.utils.writeInsert([modified]);
              }),
            );
          } catch (error) {
            const name = hiveEntityName(
              transaction.mutations[0].modified,
              queryClient,
            );
            toast({ title: `Could not save ${name}`, type: "error" });
            throw error;
          }

          return { refetch: false };
        },
        onUpdate: async ({ transaction, collection }) => {
          try {
            await Promise.all(
              transaction.mutations.map(async ({ modified }) => {
                await putHive(modified);

                collection.utils.writeUpdate(modified);
              }),
            );
          } catch (error) {
            const name = hiveEntityName(
              transaction.mutations[0].modified,
              queryClient,
            );
            toast({ title: `Could not save ${name}`, type: "error" });
            throw error;
          }

          return { refetch: false };
        },
        onDelete: async ({ transaction, collection }) => {
          try {
            await Promise.all(
              transaction.mutations.map(async ({ modified }) => {
                await deleteHive(modified);

                collection.utils.writeDelete(modified.id);
              }),
            );
          } catch (error) {
            const name = hiveEntityName(
              transaction.mutations[0].modified,
              queryClient,
            );
            toast({ title: `Could not save ${name}`, type: "error" });
            throw error;
          }

          return { refetch: false };
        },
      }),
    );
  }, [queryClient]);
}

export type HivesCollection = ReturnType<typeof useHivesCollectionInstance>;
